import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SignOut, Plus, PencilSimple, Trash, ArrowSquareOut } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (str) => str.split("\n").map((s) => s.trim()).filter(Boolean);

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wider text-faint">{label}</Label>
      {children}
    </div>
  );
}

function ImageUpload({ label, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch { toast.error("Error al subir imagen"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wider text-faint">{label}</Label>
      <div className="flex items-center gap-4">
        <div className="w-20 h-24 rounded-md overflow-hidden bg-stone-100 border border-border shrink-0">
          {value ? <img src={value} alt={label} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-faint text-xs">Sin imagen</div>}
        </div>
        <div className="space-y-2">
          <input type="file" accept="image/*" onChange={upload} data-testid={`upload-${label}`} className="block text-sm text-subtle file:mr-3 file:rounded-full file:border-0 file:bg-ink file:text-stone-50 file:px-4 file:py-2 file:text-sm file:cursor-pointer" />
          {busy && <p className="text-xs text-faint">Subiendo…</p>}
          {value && <button type="button" onClick={() => onChange("")} className="text-xs text-faint underline">Quitar</button>}
        </div>
      </div>
    </div>
  );
}

function ContentTab() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/content").then((r) => setC(r.data)); }, []);
  if (!c) return <p className="text-subtle">Cargando…</p>;

  const upd = (path, value) => {
    setC((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      let obj = copy;
      const keys = path.split(".");
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/content", c); toast.success("Contenido guardado"); }
    catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-10 max-w-3xl">
      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Hero</h3>
        <Field label="Eyebrow"><Input data-testid="c-hero-eyebrow" value={c.hero.eyebrow} onChange={(e) => upd("hero.eyebrow", e.target.value)} /></Field>
        <Field label="Título (una línea por renglón)"><Textarea data-testid="c-hero-title" value={toLines(c.hero.title_lines)} onChange={(e) => upd("hero.title_lines", fromLines(e.target.value))} /></Field>
        <Field label="Subtítulo"><Textarea data-testid="c-hero-subtitle" value={c.hero.subtitle} onChange={(e) => upd("hero.subtitle", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="CTA principal"><Input value={c.hero.cta_primary} onChange={(e) => upd("hero.cta_primary", e.target.value)} /></Field>
          <Field label="CTA secundario"><Input value={c.hero.cta_secondary} onChange={(e) => upd("hero.cta_secondary", e.target.value)} /></Field>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Posicionamiento</h3>
        <Field label="Frases (una por renglón)"><Textarea value={toLines(c.positioning.lines)} onChange={(e) => upd("positioning.lines", fromLines(e.target.value))} /></Field>
      </section>

      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Fundador</h3>
        <Field label="Nombre"><Input value={c.founder.name} onChange={(e) => upd("founder.name", e.target.value)} /></Field>
        <Field label="Rol"><Input value={c.founder.role} onChange={(e) => upd("founder.role", e.target.value)} /></Field>
        <Field label="Título"><Input value={c.founder.heading} onChange={(e) => upd("founder.heading", e.target.value)} /></Field>
        <Field label="Párrafos (uno por renglón)"><Textarea rows={4} value={toLines(c.founder.paragraphs)} onChange={(e) => upd("founder.paragraphs", fromLines(e.target.value))} /></Field>
      </section>

      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Audiencia</h3>
        <Field label="Título"><Input value={c.audience.heading} onChange={(e) => upd("audience.heading", e.target.value)} /></Field>
        <Field label="Profesionales (uno por renglón)"><Textarea value={toLines(c.audience.professionals)} onChange={(e) => upd("audience.professionals", fromLines(e.target.value))} /></Field>
        <Field label="Empresas (uno por renglón)"><Textarea value={toLines(c.audience.companies)} onChange={(e) => upd("audience.companies", fromLines(e.target.value))} /></Field>
      </section>

      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Diferenciador & Marquee</h3>
        <Field label="Título diferenciador"><Input value={c.differentiator.heading} onChange={(e) => upd("differentiator.heading", e.target.value)} /></Field>
        <Field label="Texto diferenciador"><Textarea value={c.differentiator.text} onChange={(e) => upd("differentiator.text", e.target.value)} /></Field>
        <Field label="Texto marquee"><Input value={c.marquee_text} onChange={(e) => upd("marquee_text", e.target.value)} /></Field>
      </section>

      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Contacto</h3>
        <Field label="Título"><Input value={c.contact.heading} onChange={(e) => upd("contact.heading", e.target.value)} /></Field>
        <Field label="Subtítulo"><Textarea value={c.contact.subtitle} onChange={(e) => upd("contact.subtitle", e.target.value)} /></Field>
        <Field label="Email"><Input value={c.contact.email} onChange={(e) => upd("contact.email", e.target.value)} /></Field>
      </section>

      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Chatbot IA</h3>
        <Field label="Mensaje de bienvenida"><Input value={c.chatbot.welcome} onChange={(e) => upd("chatbot.welcome", e.target.value)} /></Field>
        <Field label="Instrucciones del asistente (system prompt)"><Textarea rows={5} value={c.chatbot.system_prompt} onChange={(e) => upd("chatbot.system_prompt", e.target.value)} /></Field>
      </section>

      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Selector de Sector</h3>
        <Field label="Título"><Input value={c.sectors?.heading || ""} onChange={(e) => upd("sectors.heading", e.target.value)} /></Field>
        <Field label="Subtítulo"><Textarea value={c.sectors?.subtitle || ""} onChange={(e) => upd("sectors.subtitle", e.target.value)} /></Field>
      </section>

      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Métricas (Tablero)</h3>
        <Field label="Título"><Input value={c.metrics?.heading || ""} onChange={(e) => upd("metrics.heading", e.target.value)} /></Field>
        <Field label="Subtítulo"><Textarea value={c.metrics?.subtitle || ""} onChange={(e) => upd("metrics.subtitle", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          {(c.metrics?.stats || []).map((st, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 items-end border border-border rounded-lg p-3">
              <Field label={`Métrica ${i + 1} valor`}><Input value={st.value} onChange={(e) => { const arr = [...c.metrics.stats]; arr[i] = { ...arr[i], value: e.target.value }; upd("metrics.stats", arr); }} /></Field>
              <Field label="Etiqueta"><Input value={st.label} onChange={(e) => { const arr = [...c.metrics.stats]; arr[i] = { ...arr[i], label: e.target.value }; upd("metrics.stats", arr); }} /></Field>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Llamado a la acción (CTA)</h3>
        <Field label="Título"><Input value={c.cta?.heading || ""} onChange={(e) => upd("cta.heading", e.target.value)} /></Field>
        <Field label="Subtítulo"><Textarea value={c.cta?.subtitle || ""} onChange={(e) => upd("cta.subtitle", e.target.value)} /></Field>
        <Field label="Texto del botón"><Input value={c.cta?.button || ""} onChange={(e) => upd("cta.button", e.target.value)} /></Field>
      </section>

      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Centro de Ayuda (FAQ)</h3>
        <Field label="Título"><Input value={c.faq?.heading || ""} onChange={(e) => upd("faq.heading", e.target.value)} /></Field>
        <Field label="Subtítulo"><Textarea value={c.faq?.subtitle || ""} onChange={(e) => upd("faq.subtitle", e.target.value)} /></Field>
        {(c.faq?.items || []).map((it, i) => (
          <div key={i} className="border border-border rounded-lg p-4 space-y-3">
            <Field label={`Pregunta ${i + 1}`}><Input value={it.q} onChange={(e) => { const arr = [...c.faq.items]; arr[i] = { ...arr[i], q: e.target.value }; upd("faq.items", arr); }} /></Field>
            <Field label="Respuesta"><Textarea value={it.a} onChange={(e) => { const arr = [...c.faq.items]; arr[i] = { ...arr[i], a: e.target.value }; upd("faq.items", arr); }} /></Field>
          </div>
        ))}
      </section>

      <section className="space-y-4">
        <h3 className="font-serif text-2xl text-ink">Imágenes</h3>
        <p className="font-sans text-sm text-faint">Sube tu fotografía para el hero y la sección "¿Por qué nosotros?".</p>
        <ImageUpload label="Foto Hero" value={c.images?.hero || ""} onChange={(url) => setC((p) => ({ ...p, images: { ...(p.images || {}), hero: url } }))} />
        <ImageUpload label="Foto Fundador" value={c.images?.founder || ""} onChange={(url) => setC((p) => ({ ...p, images: { ...(p.images || {}), founder: url } }))} />
      </section>

      <Button data-testid="save-content" onClick={save} disabled={saving} className="rounded-full">{saving ? "Guardando…" : "Guardar cambios"}</Button>
    </div>
  );
}

function ServicesTab() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/services").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit({ number: "", title: "", description: "", items: [], order: items.length }); setOpen(true); };
  const openEdit = (s) => { setEdit({ ...s }); setOpen(true); };

  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/services/${edit.id}`, edit);
      else await api.post("/admin/services", edit);
      toast.success("Servicio guardado"); setOpen(false); load();
    } catch { toast.error("Error"); }
  };
  const del = async (id) => { await api.delete(`/admin/services/${id}`); toast.success("Eliminado"); load(); };

  return (
    <div className="max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-serif text-2xl text-ink">Servicios</h3>
        <Button data-testid="add-service" onClick={openNew} className="rounded-full gap-2"><Plus size={16} /> Añadir</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Título</TableHead><TableHead>Descripción</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((s) => (
            <TableRow key={s.id} data-testid={`row-service-${s.number}`}>
              <TableCell className="font-medium">{s.number}</TableCell>
              <TableCell>{s.title}</TableCell>
              <TableCell className="text-subtle max-w-xs truncate">{s.description}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <Button variant="ghost" size="icon" onClick={() => openEdit(s)}><PencilSimple size={16} /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(s.id)}><Trash size={16} /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{edit?.id ? "Editar" : "Nuevo"} servicio</DialogTitle><DialogDescription>Define el número, título, descripción e items del servicio.</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Número"><Input value={edit.number} onChange={(e) => setEdit({ ...edit, number: e.target.value })} /></Field>
                <div className="col-span-2"><Field label="Título"><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></Field></div>
              </div>
              <Field label="Descripción"><Textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
              <Field label="Items (uno por renglón)"><Textarea value={toLines(edit.items)} onChange={(e) => setEdit({ ...edit, items: fromLines(e.target.value) })} /></Field>
            </div>
          )}
          <DialogFooter><Button data-testid="save-service" onClick={save} className="rounded-full">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlansTab() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/plans").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const openNew = () => { setEdit({ name: "", price: "", target: "", features: [], highlight: false, order: items.length }); setOpen(true); };
  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/plans/${edit.id}`, edit);
      else await api.post("/admin/plans", edit);
      toast.success("Plan guardado"); setOpen(false); load();
    } catch { toast.error("Error"); }
  };
  const del = async (id) => { await api.delete(`/admin/plans/${id}`); toast.success("Eliminado"); load(); };

  return (
    <div className="max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-serif text-2xl text-ink">Planes</h3>
        <Button data-testid="add-plan" onClick={openNew} className="rounded-full gap-2"><Plus size={16} /> Añadir</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Precio</TableHead><TableHead>Para</TableHead><TableHead>Ideal</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((p) => (
            <TableRow key={p.id} data-testid={`row-plan-${p.name}`}>
              <TableCell className="font-medium">{p.name}</TableCell>
              <TableCell>{p.price}</TableCell>
              <TableCell>{p.target}</TableCell>
              <TableCell>{p.highlight ? "Sí" : "—"}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <Button variant="ghost" size="icon" onClick={() => { setEdit({ ...p }); setOpen(true); }}><PencilSimple size={16} /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(p.id)}><Trash size={16} /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{edit?.id ? "Editar" : "Nuevo"} plan</DialogTitle><DialogDescription>Configura nombre, precio, público y características del plan.</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Nombre"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
                <Field label="Precio"><Input value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} placeholder="199€" /></Field>
                <Field label="Para"><Input value={edit.target} onChange={(e) => setEdit({ ...edit, target: e.target.value })} /></Field>
              </div>
              <Field label="Características (una por renglón)"><Textarea rows={5} value={toLines(edit.features)} onChange={(e) => setEdit({ ...edit, features: fromLines(e.target.value) })} /></Field>
              <div className="flex items-center gap-3"><Switch checked={edit.highlight} onCheckedChange={(v) => setEdit({ ...edit, highlight: v })} /><Label>Destacar como "Ideal"</Label></div>
            </div>
          )}
          <DialogFooter><Button data-testid="save-plan" onClick={save} className="rounded-full">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BlogTab() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/blog?all=true").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const openNew = () => { setEdit({ slug: "", title: "", excerpt: "", content: "", cover_image: "", published: true }); setOpen(true); };
  const save = async () => {
    if (!edit.slug || !edit.title) { toast.error("Slug y título requeridos"); return; }
    try {
      if (edit.id) await api.put(`/admin/blog/${edit.id}`, edit);
      else await api.post("/admin/blog", edit);
      toast.success("Artículo guardado"); setOpen(false); load();
    } catch { toast.error("Error"); }
  };
  const del = async (id) => { await api.delete(`/admin/blog/${id}`); toast.success("Eliminado"); load(); };

  return (
    <div className="max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-serif text-2xl text-ink">Blog</h3>
        <Button data-testid="add-post" onClick={openNew} className="rounded-full gap-2"><Plus size={16} /> Nuevo artículo</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Título</TableHead><TableHead>Slug</TableHead><TableHead>Estado</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((p) => (
            <TableRow key={p.id} data-testid={`row-post-${p.slug}`}>
              <TableCell className="font-medium">{p.title}</TableCell>
              <TableCell className="text-subtle">{p.slug}</TableCell>
              <TableCell>{p.published ? "Publicado" : "Borrador"}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <Button variant="ghost" size="icon" onClick={() => { setEdit({ ...p }); setOpen(true); }}><PencilSimple size={16} /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(p.id)}><Trash size={16} /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{edit?.id ? "Editar" : "Nuevo"} artículo</DialogTitle><DialogDescription>Redacta el artículo del blog: título, slug, extracto e imagen.</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Título"><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></Field>
                <Field label="Slug (URL)"><Input value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} /></Field>
              </div>
              <Field label="Extracto"><Textarea value={edit.excerpt} onChange={(e) => setEdit({ ...edit, excerpt: e.target.value })} /></Field>
              <Field label="Imagen (URL)"><Input value={edit.cover_image} onChange={(e) => setEdit({ ...edit, cover_image: e.target.value })} /></Field>
              <Field label="Contenido (párrafos separados por línea en blanco)"><Textarea rows={8} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} /></Field>
              <div className="flex items-center gap-3"><Switch checked={edit.published} onCheckedChange={(v) => setEdit({ ...edit, published: v })} /><Label>Publicado</Label></div>
            </div>
          )}
          <DialogFooter><Button data-testid="save-post" onClick={save} className="rounded-full">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MessagesTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/contacts").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/admin/contacts/${id}`); load(); };
  return (
    <div className="max-w-4xl">
      <h3 className="font-serif text-2xl text-ink mb-6">Mensajes de contacto</h3>
      {items.length === 0 && <p className="text-subtle">Aún no hay mensajes.</p>}
      <div className="space-y-4">
        {items.map((m) => (
          <div key={m.id} data-testid={`message-${m.id}`} className="border border-border rounded-lg p-5 bg-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium text-ink">{m.name} · <a className="text-subtle underline" href={`mailto:${m.email}`}>{m.email}</a></p>
                {m.phone && <p className="text-sm text-faint">{m.phone}</p>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(m.id)}><Trash size={16} /></Button>
            </div>
            <p className="text-subtle mt-3">{m.message}</p>
            <p className="text-xs text-faint mt-2">{new Date(m.created_at).toLocaleString("es-ES")}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DiagnosticsTab() {
  const [items, setItems] = useState([]);
  const [sectorMap, setSectorMap] = useState({});
  const load = () => api.get("/admin/diagnostics").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/content").then((r) => {
      const map = {};
      (r.data?.sectors?.items || []).forEach((s) => { map[s.key] = s.label; });
      setSectorMap(map);
    }).catch(() => {});
  }, []);
  const del = async (id) => { await api.delete(`/admin/diagnostics/${id}`); load(); };
  return (
    <div className="max-w-4xl">
      <h3 className="font-serif text-2xl text-ink mb-6">Solicitudes de Diagnóstico</h3>
      {items.length === 0 && <p className="text-subtle">Aún no hay solicitudes.</p>}
      <div className="space-y-4">
        {items.map((m) => (
          <div key={m.id} data-testid={`diagnostic-${m.id}`} className="border border-border rounded-lg p-5 bg-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium text-ink">{m.name} · <a className="text-subtle underline" href={`mailto:${m.email}`}>{m.email}</a></p>
                <p className="text-sm text-faint">{m.phone} {m.company && `· ${m.company}`}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(m.id)}><Trash size={16} /></Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs px-2.5 py-1 rounded-full bg-ink text-stone-50">{sectorMap[m.sector] || m.sector}</span>
              {m.objectives?.map((o, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-subtle">{o}</span>)}
            </div>
            {m.goal && <p className="text-subtle mt-3">{m.goal}</p>}
            <p className="text-xs text-faint mt-2">{new Date(m.created_at).toLocaleString("es-ES")}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  if (loading || !user) return <div className="min-h-screen bg-stone-50" />;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-border bg-white sticky top-0 z-40">
        <div className="px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-2xl text-ink">DOMMAR</span>
            <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-faint">Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="rounded-full gap-2" onClick={() => window.open("/", "_blank")}><ArrowSquareOut size={14} /> Ver web</Button>
            <Button data-testid="logout" variant="ghost" size="sm" className="rounded-full gap-2" onClick={() => { logout(); navigate("/"); }}><SignOut size={14} /> Salir</Button>
          </div>
        </div>
      </header>

      <main className="px-6 md:px-10 py-10">
        <h1 className="font-serif text-4xl text-ink mb-2">Hola, {user.name?.split(" ")[0]}</h1>
        <p className="font-sans text-subtle mb-8">Edita todo el contenido de tu website sin tocar código.</p>
        <Tabs defaultValue="content">
          <TabsList className="mb-8 flex-wrap h-auto">
            <TabsTrigger value="content" data-testid="tab-content">Contenido</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">Servicios</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans">Planes</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Mensajes</TabsTrigger>
            <TabsTrigger value="diagnostics" data-testid="tab-diagnostics">Diagnósticos</TabsTrigger>
          </TabsList>
          <TabsContent value="content"><ContentTab /></TabsContent>
          <TabsContent value="services"><ServicesTab /></TabsContent>
          <TabsContent value="plans"><PlansTab /></TabsContent>
          <TabsContent value="blog"><BlogTab /></TabsContent>
          <TabsContent value="messages"><MessagesTab /></TabsContent>
          <TabsContent value="diagnostics"><DiagnosticsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
